/**
 * Auth Store (Zustand)
 *
 * Manages authentication state including:
 * - User information
 * - Authentication status
 * - Login/logout actions
 * - Token management
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { authService } from '@/api/services/auth.service';
import { usePermissionStore } from './permissionSlice';
import type { CurrentUser, LoginRequest } from '@/api/types';
import {
  setStoredToken,
  setStoredRefreshToken,
  clearAuthStorage,
  getStoredToken,
} from '@/utils/storage.utils';

// ============================================
// State Types
// ============================================

interface AuthState {
  /** Current authenticated user */
  user: CurrentUser | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Loading state for auth operations */
  isLoading: boolean;
  /** Error message from last operation */
  error: string | null;
  /** Whether initial auth check has completed */
  isInitialized: boolean;
}

interface AuthActions {
  /** Login with credentials */
  login: (credentials: LoginRequest) => Promise<void>;
  /** Logout and clear session */
  logout: () => void;
  /** Fetch current user details */
  fetchCurrentUser: () => Promise<void>;
  /** Clear error message */
  clearError: () => void;
  /** Initialize auth state (check existing token) */
  initialize: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

// ============================================
// Initial State
// ============================================

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
};

// ============================================
// Store
// ============================================

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,

      // ============================================
      // Actions
      // ============================================

      /**
       * Login with credentials
       */
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });

        try {
          // Login endpoint returns { token, refreshToken } directly
          const { token, refreshToken } = await authService.login(credentials);

          if (token && refreshToken) {
            // Store tokens in sessionStorage
            setStoredToken(token);
            setStoredRefreshToken(refreshToken);

            // Fetch user details
            await get().fetchCurrentUser();

            // RBAC: load this user's effective permissions for UI gating
            void usePermissionStore.getState().load({ force: true });

            set({ isAuthenticated: true, isLoading: false });
          } else {
            throw new Error('Login failed: No tokens received');
          }
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'An error occurred during login';
          set({
            error: message,
            isAuthenticated: false,
            user: null,
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Logout and clear session
       */
      logout: () => {
        // Clear tokens from storage
        clearAuthStorage();

        // RBAC: drop the cached permission set
        usePermissionStore.getState().reset();

        // Reset state
        set({
          ...initialState,
          isInitialized: true,
        });

        // Redirect to login
        window.location.href = '/login';
      },

      /**
       * Fetch current user details
       */
      fetchCurrentUser: async () => {
        try {
          const response = await authService.getCurrentUser();

          if (response.status === 'success' && response.result) {
            set({
              user: response.result,
              isAuthenticated: true,
            });
          } else {
            throw new Error('Failed to fetch user details');
          }
        } catch (error) {
          console.error('Failed to fetch current user:', error);
          // Don't logout here - the axios interceptor will handle 401
          throw error;
        }
      },

      /**
       * Clear error message
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Initialize auth state
       * Check if there's an existing token and fetch user if so
       */
      initialize: async () => {
        const token = getStoredToken();

        if (!token) {
          set({ isInitialized: true, isAuthenticated: false });
          return;
        }

        try {
          await get().fetchCurrentUser();

          // RBAC: restore the permission set for the existing session
          void usePermissionStore.getState().load({ force: true });

          set({ isInitialized: true });
        } catch {
          // Token might be expired, clear storage
          clearAuthStorage();
          set({
            isInitialized: true,
            isAuthenticated: false,
            user: null,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist specific fields (not tokens - they're in sessionStorage directly)
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        // Don't persist user object - always fetch fresh
      }),
    }
  )
);

// ============================================
// Selectors (for convenience)
// ============================================

/**
 * Get current user from store
 */
export const selectUser = (state: AuthStore) => state.user;

/**
 * Get authentication status
 */
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated;
